import React, { useState, useEffect, useRef } from "react";
import "./solutions.css";

export const Solutions = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const solutionsRef = useRef(null);

    // Solutions data array with appropriate icons
    const solutions = [
        {
            id: 1,
            title: "Loan Management System",
            icon: "loan-icon",
            color: "#4285F4",
            description: "Loan management system that automates the entire lending lifecycle—from loan booking and disbursal to repayment tracking, compliance, and reporting. Manage personal loans, business financing, and co-lending—all in one place.",
            features: [
                "Scalable & trusted",
                "Complete loan & financial management",
                "Built-in compliance & risk monitoring",
                "Accounting, billing & taxation",
                "Custom workflows & role-based access"
            ],
            image: "/img/Pasted image (5).png"
        },
        {
            id: 2,
            title: "Hospital Information System (HIS)",
            icon: "hospital-icon",
            color: "#EA4335",
            description: "Easy-to-use hospital information system (HIS) for all healthcare organizations.",
            features: [
                "Patient Appointments",
                "Out Patient management",
                "Inpatient management",
                "Clinical Procedures",
                "Rehabilitation and Physiotherapy",
                "Laboratory Information System",
                "Medical Code Standards"
            ],
            image: "/img/Pasted image (4).png"
        },
        {
            id: 3,
            title: "Pharmacy Information System (PIS)",
            icon: "pharmacy-icon",
            color: "#34A853",
            description: "A custom app to manage inventory and billing system for pharmacy",
            features: [
                "Inventory tracking",
                "Inventory management",
                "Prescription management",
                "Billing and invoicing",
                "Reporting and analytics"
            ],
            image: "/img/Pasted image (4).png"
        },
        {
            id: 5,
            title: "School Management System",
            icon: "school-icon",
            color: "#7B68EE",
            description: "User-friendly Education Management System designed to streamline the administrative and academic processes of educational institutions.",
            features: [
                "Student & Teacher Management",
                "Admission Management",
                "Fee Management",
                "Course Scheduling & Exam Planning",
                "Student Portal"
            ],
            image: "/img/Pasted image (3).png"
        }
    ];

    // Improved Intersection Observer for animations
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    // Once visible, disconnect observer to keep animations active
                    observer.disconnect();
                }
            },
            { threshold: 0.1, rootMargin: "0px" }
        );

        const currentRef = solutionsRef.current;

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, []);

    // Icon renderer function with more appropriate icons
    const renderIcon = (iconName) => {
        switch (iconName) {
            case "loan-icon":
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="6" width="20" height="12" rx="2" />
                        <path d="M12 12h.01" />
                        <path d="M17 12h.01" />
                        <path d="M7 12h.01" />
                        <path d="M2 10h20" />
                    </svg>
                );
            case "hospital-icon":
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                        <rect x="9" y="9" width="6" height="6" />
                        <line x1="12" y1="9" x2="12" y2="15" />
                        <line x1="9" y1="12" x2="15" y2="12" />
                    </svg>
                );
            case "pharmacy-icon":
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                        <path d="M12 11v8" />
                        <path d="M8 15h8" />
                    </svg>
                );
            case "digital-icon":
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 17a1 1 0 1 0 2 0 1 1 0 1 0-2 0Z" />
                        <rect width="20" height="14" x="2" y="3" rx="2" />
                        <path d="M22 8h-9" />
                        <path d="M2 8h7" />
                        <path d="M22 12h-4" />
                        <path d="M2 12h4" />
                        <path d="M21 17h1" />
                        <path d="M2 17h1" />
                    </svg>
                );
            case "school-icon":
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m2 22 10-10 10 10" />
                        <path d="M4 15v-3a8 8 0 0 1 16 0v3" />
                        <path d="M12 4V2" />
                    </svg>
                );
            default:
                return null;
        }
    };

    // Applying the animate class immediately for initial render when needed
    const sectionHeaderClass = `section-header ${isVisible ? 'animate-fade-in' : ''}`;
    const solutionsTabsClass = `solutions-tabs-container ${isVisible ? 'animate-slide-up' : ''}`;
    const ctaContainerClass = `cta-container ${isVisible ? 'animate-fade-in' : ''}`;

    return (
        <section id="solutions" className="solutions-section" ref={solutionsRef}>
            <div className="solutions-container">
                {/* Modernized header with guaranteed visibility */}
                <div className={sectionHeaderClass} style={{ opacity: 1 }}>
                    <h2 className="section-title">OUR SOLUTIONS</h2>
                    <div className="title-underline">
                        <span className="title-underline-highlight"></span>
                    </div>
                    <p className="section-subtitle">
                        Cutting-edge technology solutions tailored to drive your business forward
                    </p>
                </div>

                {/* Solutions tabs */}
                <div className={solutionsTabsClass}>
                    <div className="solutions-tabs">
                        {solutions.map((solution, index) => (
                            <button
                                key={solution.id}
                                className={`tab-button ${activeTab === index ? 'active' : ''}`}
                                onClick={() => setActiveTab(index)}
                                style={{
                                    '--tab-color': solution.color,
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                <span className="tab-icon" style={{ color: activeTab === index ? '#fff' : solution.color }}>
                                    {renderIcon(solution.icon)}
                                </span>
                                <span className="tab-title">{solution.title}</span>
                            </button>
                        ))}
                    </div>

                    {/* Solutions content */}
                    <div className="solutions-content">
                        {solutions.map((solution, index) => (
                            <div
                                key={solution.id}
                                className={`solution-panel ${activeTab === index ? 'active' : ''}`}
                                style={{
                                    '--panel-color': solution.color
                                }}
                            >
                                <div className="solution-info">
                                    <h3 className="solution-title">
                                        <span className="solution-icon" style={{ backgroundColor: solution.color }}>
                                            {renderIcon(solution.icon)}
                                        </span>
                                        {solution.title}
                                    </h3>
                                    <p className="solution-description">{solution.description}</p>

                                    <div className="solution-features">
                                        <h4 className="features-title">Key Features</h4>
                                        <ul className="features-list">
                                            {solution.features.map((feature, idx) => (
                                                <li key={idx} className="feature-item">
                                                    <span className="feature-bullet" style={{ backgroundColor: solution.color }}></span>
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <button className="learn-more-btn" style={{ backgroundColor: solution.color }}>
                                        Learn More
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M5 12h14M12 5l7 7-7 7"/>
                                        </svg>
                                    </button>
                                </div>

                                <div className="solution-image-wrapper">
                                    <div className="solution-image-container">
                                        <div className="image-overlay" style={{ backgroundColor: solution.color }}></div>
                                        <img
                                            src={solution.image}
                                            alt={solution.title}
                                            className="solution-image"
                                            loading="lazy"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className={ctaContainerClass}>
                    <h3 className="cta-title">Ready to transform your business?</h3>
                    <p className="cta-subtitle">Let's discuss how our solutions can address your specific challenges</p>
                    <button className="cta-button">
                        Schedule a Consultation
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14M12 5l7 7-7 7"/>
                        </svg>
                    </button>
                </div>
            </div>
        </section>
    );
};

export default Solutions;